import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators } from "@angular/forms";
import { BoolOrNull, DateOrNull, ErrorMessage, StringOrNull } from "@core-interfaces/global";
import { IchangePasswordForm, IforgotPasswordForm, InewPassForm, IuserForm, IuserService } from "@core-interfaces/services/user";
import { IchangePasswordInput, IforgotPasswordInput, InewPassInput, IuserInput } from "@core-ports/inputs/user";
import { IuserResponse, IusersResponse } from "@core-ports/outputs/user";
import { environment } from "@infra-env/environment";
import { firstValueFrom, map, Observable } from "rxjs";

@Injectable({
    providedIn: 'root',
})
export class UserManager implements IuserService {
    private readonly USER_API_URL = `${environment.apiUrl}/user`;

    readonly errorMessages: ErrorMessage = {
        username: {
            required: "El nombre de usuario es requerido"
        },
        password: {
            required: "La contraseña es requerida",
        },
        confirm_password: {
            required: "La confirmación de la contraseña es requerida",
            isMatching: "Las contraseñas no coinciden",
        },
        role: {
            required: "El rol es requerido",
        },
        first_name: {
            required: "El nombre es requerido",
        },
        last_name: {
            required: "El apellido es requerido",
        },
        email: {
            required: "El correo es requerido",
        },
        rut: {
            required: "El RUT es requerido",
            invalidRut: "El RUT es invalido",
        },
        birth_date: {
            required: "La fecha de nacimiento es requerida",
        },
        phone: {
            required: "El número de teléfono es requerido",
        },
        region: {
            required: "La region es requerida",
        },
        commune: {
            required: "La comuna es requerida",
        },
        nationality: {
            required: "La nacionalidad es requerida",
        },
        address: {
            required: "La dirección es requerida",
        },
        terms: {
            requiredTrue: "Debes aceptar los términos y condiciones",
        },
        new_password: {
            required: "La nueva contraseña es requerida",
        },
        confirm_new_password: {
            required: "La confirmación de la nueva contraseña es requerida",
            isMatching: "Las contraseñas no coinciden",
        }
    };

    userForm: FormGroup<IuserForm>;
    changePasswordForm: FormGroup<IchangePasswordForm>;
    forgotForm: FormGroup<IforgotPasswordForm>;
    newPassForm: FormGroup<InewPassForm>;

    constructor(
        private http: HttpClient,
        private fb: FormBuilder,
    ) {
        this.userForm = this.fb.group<IuserForm>({
            username: this.fb.control<StringOrNull>(null, {
                validators: [Validators.required],
            }),
            password: this.fb.control<StringOrNull>(null, {
                validators: [Validators.required],
            }),
            confirm_password: this.fb.control<StringOrNull>(null, {
                validators: [Validators.required, this.matchValues('password')],
            }),
            role: this.fb.control<StringOrNull>(null, {
                validators: [Validators.required],
            }),
            first_name: this.fb.control<StringOrNull>(null, {
                validators: [Validators.required],
            }),
            last_name: this.fb.control<StringOrNull>(null, {
                validators: [Validators.required],
            }),
            email: this.fb.control<StringOrNull>(null, {
                validators: [Validators.required],
            }),
            rut: this.fb.control<StringOrNull>(null, {
                validators: [Validators.required, this.validateRut()],
            }),
            birth_date: this.fb.control<DateOrNull>(null, {
                validators: [Validators.required],
            }),
            phone: this.fb.control<StringOrNull>(null, {
                validators: [Validators.required],
            }),
            region: this.fb.control<StringOrNull>(null, {
                validators: [Validators.required],
            }),
            commune: this.fb.control<StringOrNull>(null, {
                validators: [Validators.required],
            }),
            nationality: this.fb.control<StringOrNull>(null, {
                validators: [Validators.required],
            }),
            address: this.fb.control<StringOrNull>(null, {
                validators: [Validators.required],
            }),
            terms: this.fb.control<BoolOrNull>(null, {
                validators: [Validators.requiredTrue]
            })
        });

        this.changePasswordForm = this.fb.group<IchangePasswordForm>({
            password: this.fb.control<StringOrNull>(null, {
                validators: [Validators.required],
            }),
            new_password: this.fb.control<StringOrNull>(null, {
                validators: [Validators.required],
            }),
            confirm_new_password: this.fb.control<StringOrNull>(null, {
                validators: [Validators.required, this.matchValues('new_password')]
            }),
        });

        this.forgotForm = this.fb.group<IforgotPasswordForm>({
            email: this.fb.control<StringOrNull>(null, {
                validators: [Validators.required],
            })
        });

        this.newPassForm = this.fb.group<InewPassForm>({
            token: this.fb.control<StringOrNull>(null, {
                validators: [Validators.required],
            }),
            new_password: this.fb.control<StringOrNull>(null, {
                validators: [Validators.required],
            }),
            confirm_new_password: this.fb.control<StringOrNull>(null, {
                validators: [Validators.required, this.matchValues('new_password')]
            }),
        });

    }

    getErrorMessage(controlName: keyof IuserForm | string): string | null {
        const inputControl = this.userForm.get(controlName);
    
        if (inputControl && inputControl.errors) {
            for (const errorKey in inputControl.errors) {
                if (errorKey === 'serverError') {
                    return (
                        this.errorMessages[controlName]['serverError'] ||
                        'Error en el servidor.'
                    );
                }
                if (this.errorMessages[controlName]?.[errorKey]) {
                    return this.errorMessages[controlName][errorKey];
                }
            }
        }
        return null;
    }

    getErrorMessagePassChange(controlName: keyof IchangePasswordInput | string): string | null {
        const inputControl = this.changePasswordForm.get(controlName);
    
        if (inputControl && inputControl.errors) {
            for (const errorKey in inputControl.errors) {
                if (errorKey === 'serverError') {
                    return (
                        this.errorMessages[controlName]['serverError'] ||
                        'Error en el servidor.'
                    );
                }
                if (this.errorMessages[controlName]?.[errorKey]) {
                    return this.errorMessages[controlName][errorKey];
                }
            }
        }
        return null;
    }

    getErrorMessageNewPass(controlName: keyof InewPassForm | string): string | null {
        const inputControl = this.newPassForm.get(controlName);
    
        if (inputControl && inputControl.errors) {
            for (const errorKey in inputControl.errors) {
                if (errorKey === 'serverError') {
                    return (
                        this.errorMessages[controlName]['serverError'] ||
                        'Error en el servidor.'
                    );
                }
                if (this.errorMessages[controlName]?.[errorKey]) {
                    return this.errorMessages[controlName][errorKey];
                }
            }
        }
        return null;
    }

    async createUser(input: IuserInput): Promise<Observable<IuserResponse>> {
        return this.http.post<IuserResponse>(this.USER_API_URL, input).pipe(
            map((response) => {
                if(response){
                    return response;
                } else{
                    throw new Error("No se puede crear el usuario");
                }
            })
        )
    }

    async getUsers(): Promise<Observable<IusersResponse>> {
        return this.http.get<IusersResponse>(this.USER_API_URL).pipe(
            map((response) => {
                if(response){
                    return response;
                }else{
                    throw new Error("No se pueden obtener los usuarios");
                }
            })
        )
    }

    async getClientUsers(): Promise<Observable<IusersResponse>> {
        return this.http.get<IusersResponse>(`${this.USER_API_URL}/clients`).pipe(
            map((response) => {
                if(response){
                    return response;
                }else{
                    throw new Error("No se pueden obtener los usuarios clientes");
                }
            })
        )
    }

    async getUsersFromFilter(input: any): Promise<Observable<IusersResponse>> {
        return this.http.post<IusersResponse>(`${this.USER_API_URL}/filter`, input).pipe(
            map((response) => {
                if (response){
                    return response;
                }else{
                    throw new Error("No se pueden obtener los usuarios filtrados");
                }
            })
        )
    }

    async getUserById(id: number): Promise<Observable<IuserResponse>> {
        return this.http.get<IuserResponse>(`${this.USER_API_URL}/${id}`).pipe(
            map((response) => {
                if(response){
                    return response;
                }else{
                    throw new Error("No se pueden obtener los datos del usuario");
                }
            })
        )
    }

    async uploadProfileImage(input: any): Promise<Observable<any>> {
        return this.http.post(`${this.USER_API_URL}/upload`, input).pipe(
            map((response) => {
                if(response){
                    return response;
                } else{
                    throw new Error("No se ha podido cargar la imagen correctamente");
                }
            })
        )
    }

    async changePassword(input: IchangePasswordInput): Promise<Observable<IuserResponse>> {
        return this.http.put<IuserResponse>(`${this.USER_API_URL}/change_pass`, input).pipe(
            map((response) => {
                if(response){
                    return response;
                } else{
                    throw new Error("No se puede actualizar la contraseña del usuario");
                }
            })
        )
    }

    async forgotPassword(input: IforgotPasswordInput): Promise<Observable<IuserResponse>> {
        return this.http.post<IuserResponse>(`${this.USER_API_URL}/forgot_pass`, input).pipe(
            map((response) => {
                if(response){
                    return response;
                } else {
                    throw new Error("No se puede establecer una nueva contraseña");
                }
            })
        )
    }

    async setNewPassword(input: InewPassInput): Promise<Observable<IuserResponse>> {
        return this.http.put<IuserResponse>(`${this.USER_API_URL}/set_password`, input).pipe(
            map((response) => {
                if(response){
                    return response;
                }else{
                    throw new Error("No se puede establecer una nueva contraseña");
                }
            })
        )
    }

    async getAnsweredSurveys(id: number): Promise<Observable<any>> {
        return this.http.get<any>(`${this.USER_API_URL}/${id}/get_answered_surveys`).pipe(
            map((response) => {
                if(response){
                    return response;
                }else{
                    throw new Error("No se han podido obtener las encuestas respondidas por el usuario");
                }
            })
        )
    }

    async getRedeemedRewards(id: number): Promise<Observable<any>> {
        return this.http.get<any>(`${this.USER_API_URL}/${id}/get_redeemed_rewards`).pipe(
            map((response) => {
                if(response){
                    return response;
                }else{
                    throw new Error("No se han podido obtener las recompensas canjeadas por el usuario");
                }
            })
        )
    }

    async updateUser(id: number, input: IuserInput): Promise<Observable<IuserResponse>> {
        return this.http.put<IuserResponse>(`${this.USER_API_URL}/${id}`, input).pipe(
            map((response) => {
                if(response){
                    return response;
                }else{
                    throw new Error("No se ha podido actualizar los datos del usuario");
                }
            })
        )
    }

    async generateExcelFileFromList(users: any): Promise<void> {
        try{
            const blob = await firstValueFrom(
                this.http.post(`${this.USER_API_URL}/download_list`, users, {
                    responseType: 'blob',
                })
            );

            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `listado_usuarios.xlsx`;
            a.click();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error al descargar el archivo: ', error);
        }
    }

    async getUserPoints(id: number): Promise<Observable<any>> {
        return this.http.get<any>(`${this.USER_API_URL}/${id}/get_points`).pipe(
            map((response) => {
                if(response){
                    return response;
                }else{
                    throw new Error("No se ha podido obtener los puntos del usuario");
                }
            })
        )
    }

    async getUsersBySurvey(id: number): Promise<Observable<any>> {
        return this.http.get<any>(`${this.USER_API_URL}/get_users_by_survey/${id}`).pipe(
            map((response) => {
                if(response){
                    return response;
                }else{
                    throw new Error("No se han podido obtener los usuarios que han respondido la encuesta");
                }
            })
        )
    }

    notifyGiveaway(input: any): void {
        this.http.post<any>(`${this.USER_API_URL}/notify_giveaway`, input).subscribe({
            next: (response) => {
                console.log("Notificación enviada exitosamente:", response);
            },
            error: (error) => {
                console.error("Error al notificar a los ganadores del sorteo:", error);
            }
        });
    }

    async isFirstSurveyAnswered(id: number): Promise<Observable<any>> {
        return this.http.get<any>(`${this.USER_API_URL}/${id}/is_first_survey_answered`).pipe(
            map((response) => {
                if(response){
                    return response;
                }else{
                    throw new Error("No se ha podido verificar si la primera encuesta ha sido respondida");
                }
            })
        )
    }

    private matchValues(matchTo: string): (control: AbstractControl) => ValidationErrors | null {
        return (control: AbstractControl): ValidationErrors | null => {
            return !!control.parent &&
                !!control.parent.value &&
                control.value === (control.parent?.controls as { [key: string]: AbstractControl })[matchTo]?.value
                ? null
                : { isMatching: false };
        };
    }

    private validateRut(): (control: AbstractControl) => ValidationErrors | null {
        return (control: AbstractControl): ValidationErrors | null => {
            let rut = control.value;
            if (!rut) {
                return null;
            }

            const rutPattern = /^[0-9]{1,2}\.?[0-9]{3}\.?[0-9]{3}-[0-9kK]{1}$/;
            if (!rutPattern.test(rut)) {
                return { invalidRut: true };
            }


            rut = rut.replace(/\./g, '');
            const [rutBody, verifier] = rut.split('-');

            let sum = 0;
            let multiplier = 2;

            for (let i = rutBody.length - 1; i >= 0; i--) {
                sum += parseInt(rutBody[i], 10) * multiplier;
                multiplier = multiplier < 7 ? multiplier + 1 : 2;
            }

            const mod11 = 11 - (sum % 11);
            const expectedVerifier = mod11 === 11 ? '0' : mod11 === 10 ? 'k' : mod11.toString();

            return verifier.toLowerCase() === expectedVerifier ? null : { invalidRut: true };
        };
    }

}